import { z } from 'zod'

/** TanStack Router route id for the documents page (folder index route). */
export const documentsRouteId = '/documents/' as const
/**
 * TanStack Router route id for the Dossiers page. The Dossiers page embeds the
 * same knowledge document viewer, so the viewer's URL-sync (useTabState) can
 * target this route in addition to `/documents/`.
 */
export const dossiersViewerRouteId = '/dossiers/' as const
export type DocumentViewerUrlPaths =
  | typeof documentsRouteId
  | typeof dossiersViewerRouteId

/**
 * Accepts values that were JSON-stringified one extra time (`pageNumber=%221%22`,
 * `selectedBoxes=%22%5B%5D%22`). Links shared before the auth-redirect fix in
 * returnToNavigation.ts carry params in this shape; without unwrapping, the
 * route rejects them as invalid_type and shows the broken-link page.
 */
const unwrapDoubleEncoded = <T extends z.ZodTypeAny>(schema: T): T =>
  // Cast back to T: z.preprocess widens the input type to a required
  // `unknown`, which would break every typed navigate() targeting this route.
  // The unwrapping is runtime-only leniency; declared types stay the same.
  z.preprocess((value) => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value)
      } catch {
        return value
      }
    }
    return value
  }, schema) as unknown as T

export const documentsSearchSchema = z.object({
  searchQuery: z.string().optional(),
  searchInputQuery: z.string().optional(),
  searchFilterTitle: z.string().optional(),
  searchFilterDocumentTitle: z.string().optional(),
  searchFilterSystem: z.string().optional(),
  searchFilterDrawing: z.string().optional(),
  searchFilterVendor: z.array(z.string()).optional(),
  searchFilterDocType: z.array(z.string()).optional(),
  searchTab: z.enum(['documents', 'sensors']).optional(),
  searchBrowseAll: z.coerce.boolean().optional(),
  documentID: z.string().optional(),
  pageNumber: unwrapDoubleEncoded(z.number().optional()),
  selectedBoxes: unwrapDoubleEncoded(z.array(z.string()).optional()),
  // coord quads; see utils/selectedRegionsParam.ts
  selectedRegions: z.string().optional(),
})

export type DocumentsSearchParams = z.infer<typeof documentsSearchSchema>

export const CLEAR_SEARCH_VIEWER_PARAMS: DocumentsSearchParams = {
  searchQuery: undefined,
  searchInputQuery: undefined,
  searchFilterTitle: undefined,
  searchFilterDocumentTitle: undefined,
  searchFilterSystem: undefined,
  searchFilterDrawing: undefined,
  searchFilterVendor: undefined,
  searchFilterDocType: undefined,
  searchTab: undefined,
  searchBrowseAll: undefined,
}

/** Same values as `searchTab` / search state content mode. */
export type DocumentsViewerContentFilter = 'documents' | 'sensors'

export type DocumentsViewerColumnFilter = { id: string; value: unknown }

/** TanStack table column filter list shape used for documents search URL sync. */
export type DocumentsViewerColumnFiltersState = DocumentsViewerColumnFilter[]

export function urlSearchParamsFromColumnFilters(
  filters: DocumentsViewerColumnFiltersState | undefined
): Pick<
  DocumentsSearchParams,
  | 'searchFilterTitle'
  | 'searchFilterDocumentTitle'
  | 'searchFilterSystem'
  | 'searchFilterDrawing'
  | 'searchFilterVendor'
  | 'searchFilterDocType'
> {
  const empty: Pick<
    DocumentsSearchParams,
    | 'searchFilterTitle'
    | 'searchFilterDocumentTitle'
    | 'searchFilterSystem'
    | 'searchFilterDrawing'
    | 'searchFilterVendor'
    | 'searchFilterDocType'
  > = {
    searchFilterTitle: undefined,
    searchFilterDocumentTitle: undefined,
    searchFilterSystem: undefined,
    searchFilterDrawing: undefined,
    searchFilterVendor: undefined,
    searchFilterDocType: undefined,
  }

  if (!filters?.length) {
    return empty
  }

  const byId = new Map(filters.map((f) => [f.id, f.value]))

  const title = byId.get('title')
  if (typeof title === 'string' && title.length > 0) {
    empty.searchFilterTitle = title
  }

  const documentTitle = byId.get('documentTitle')
  if (typeof documentTitle === 'string' && documentTitle.length > 0) {
    empty.searchFilterDocumentTitle = documentTitle
  }

  const system = byId.get('system')
  if (typeof system === 'string' && system.length > 0) {
    empty.searchFilterSystem = system
  }

  const drawing = byId.get('drawing')
  if (typeof drawing === 'string' && drawing.length > 0) {
    empty.searchFilterDrawing = drawing
  }

  const vendor = byId.get('vendor')
  if (Array.isArray(vendor) && vendor.length > 0) {
    empty.searchFilterVendor = vendor as string[]
  }

  const docType = byId.get('docType')
  if (Array.isArray(docType) && docType.length > 0) {
    empty.searchFilterDocType = docType as string[]
  }

  return empty
}

function hasSearchFilterParamsInUrl(search: DocumentsSearchParams): boolean {
  if (search.searchFilterTitle) return true
  if (search.searchFilterDocumentTitle) return true
  if (search.searchFilterSystem) return true
  if (search.searchFilterDrawing) return true
  if (
    search.searchFilterVendor !== undefined &&
    search.searchFilterVendor.length > 0
  ) {
    return true
  }
  if (
    search.searchFilterDocType !== undefined &&
    search.searchFilterDocType.length > 0
  ) {
    return true
  }
  return false
}

/** Inverse of {@link urlSearchParamsFromColumnFilters}: URL search params → column filters. */
export function columnFiltersFromSearchUrlParams(
  search: DocumentsSearchParams
): DocumentsViewerColumnFiltersState {
  const out: DocumentsViewerColumnFilter[] = []

  if (search.searchFilterTitle) {
    out.push({ id: 'title', value: search.searchFilterTitle })
  }
  if (search.searchFilterDocumentTitle) {
    out.push({ id: 'documentTitle', value: search.searchFilterDocumentTitle })
  }
  if (search.searchFilterSystem) {
    out.push({ id: 'system', value: search.searchFilterSystem })
  }
  if (search.searchFilterDrawing) {
    out.push({ id: 'drawing', value: search.searchFilterDrawing })
  }
  if (
    search.searchFilterVendor !== undefined &&
    search.searchFilterVendor.length > 0
  ) {
    out.push({ id: 'vendor', value: search.searchFilterVendor })
  }
  if (
    search.searchFilterDocType !== undefined &&
    search.searchFilterDocType.length > 0
  ) {
    out.push({ id: 'docType', value: search.searchFilterDocType })
  }

  return out
}

/** Partial search tab state derived from URL (for deep links). */
export type DocumentsSearchUrlStatePatch = {
  query?: string
  inputQuery?: string
  filters?: DocumentsViewerColumnFiltersState
  contentFilter?: DocumentsViewerContentFilter
  browseAll?: boolean
}

export function getSearchStatePatchFromUrl(
  search: DocumentsSearchParams
): DocumentsSearchUrlStatePatch | undefined {
  const has =
    search.searchQuery !== undefined ||
    search.searchInputQuery !== undefined ||
    hasSearchFilterParamsInUrl(search) ||
    search.searchTab !== undefined ||
    search.searchBrowseAll !== undefined
  if (!has) return undefined
  return {
    ...(search.searchQuery !== undefined && { query: search.searchQuery }),
    ...(search.searchInputQuery !== undefined
      ? { inputQuery: search.searchInputQuery }
      : search.searchQuery !== undefined
        ? { inputQuery: undefined }
        : {}),
    ...(hasSearchFilterParamsInUrl(search) && {
      filters: columnFiltersFromSearchUrlParams(search),
    }),
    ...(search.searchTab !== undefined && { contentFilter: search.searchTab }),
    ...(search.searchBrowseAll !== undefined && {
      browseAll: search.searchBrowseAll,
    }),
  }
}
